// src/pages/components/AcquirerProvenanceModal.jsx
//
// Per-acquirer field-source drill-down. Opens when broker clicks
// an acquirer's name in the BuyerSuggestionPanel. Answers "where
// did each number come from and when was it last refreshed" so a
// broker can trust or challenge the score before pitching.
//
// Backend: GET /ai/acquirers/<id>/provenance/
// Response shape documented in backend/ai_native/acquirers/views_admin.py.

import React, { useCallback, useEffect, useState } from "react";
import { request } from "../../services/server";

const _OVERLAY_STYLE = {
  position: "fixed",
  inset: 0,
  background: "rgba(28, 25, 23, 0.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
  padding: 20,
};

const _MODAL_STYLE = {
  background: "white",
  borderRadius: 8,
  boxShadow: "0 20px 40px rgba(0, 0, 0, 0.18)",
  maxWidth: 720,
  width: "100%",
  maxHeight: "80vh",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

function _formatRelative(iso) {
  if (!iso) return "—";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const days = Math.round((now - then) / (1000 * 60 * 60 * 24));
    if (days <= 0) return "idag";
    if (days === 1) return "igår";
    if (days < 7) return `${days} dagar sedan`;
    if (days < 60) {
      const weeks = Math.round(days / 7);
      return `${weeks} v sedan`;
    }
    const months = Math.round(days / 30);
    if (months < 24) return `${months} mån sedan`;
    return `${Math.round(months / 12)} år sedan`;
  } catch {
    return iso;
  }
}

function _fieldLabel(fieldName) {
  // Prettify raw field paths so brokers see Swedish labels instead
  // of "profile.target_revenue_min". Falls back to the raw name for
  // fields we haven't labelled yet.
  const map = {
    revenue: "Omsättning",
    employees: "Antal anställda",
    ebitda: "EBITDA",
    website: "Webbplats",
    contact_email: "Kontakt-e-post",
    status: "Bolagsstatus",
    ownership_type: "Ägartyp",
    headquarters_city: "Huvudkontor",
    description: "Beskrivning",
    sni_prefix: "SNI-kod",
    "profile.target_sni_prefixes": "Målsektorer (SNI)",
    "profile.target_geographies": "Målgeografier",
    "profile.target_revenue_min": "Målomsättning (min)",
    "profile.target_revenue_max": "Målomsättning (max)",
    "profile.target_ebitda_min": "Mål-EBITDA (min)",
    "profile.target_ebitda_max": "Mål-EBITDA (max)",
    recent_acquisitions: "Senaste förvärv",
  };
  return map[fieldName] || fieldName;
}

function AcquirerProvenanceModal({ acquirerId, acquirerName, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!acquirerId) return;
    setLoading(true);
    setError("");
    try {
      const payload = await request(
        `/ai/acquirers/${acquirerId}/provenance/`,
      );
      setData(payload);
    } catch (e) {
      setError(e?.message || "Kunde inte hämta datakällor.");
    } finally {
      setLoading(false);
    }
  }, [acquirerId]);

  useEffect(() => {
    load();
  }, [load]);

  // Close on Escape key.
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose && onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const displayName = data?.acquirer?.name || acquirerName || "Köpare";
  const orgNumber = data?.acquirer?.org_number || "";

  return (
    <div
      style={_OVERLAY_STYLE}
      onClick={(e) => {
        // Click outside the modal → close.
        if (e.target === e.currentTarget) onClose && onClose();
      }}
    >
      <div style={_MODAL_STYLE} role="dialog" aria-modal="true">
        {/* ── Header ── */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #e7e5e4",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: 17 }}>{displayName}</h2>
            {orgNumber && (
              <div style={{ fontSize: 12, color: "#78716c", marginTop: 4 }}>
                Org.nr {orgNumber}
              </div>
            )}
            <div style={{ fontSize: 12, color: "#57534e", marginTop: 6 }}>
              Datakällor — varifrån varje fält kommer och när det
              senast uppdaterades.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: 22,
              cursor: "pointer",
              color: "#78716c",
              lineHeight: 1,
              padding: 4,
            }}
            aria-label="Stäng"
          >
            ×
          </button>
        </div>

        {/* ── Body ── */}
        <div style={{ padding: "16px 20px", overflowY: "auto", flex: 1 }}>
          {loading && (
            <div style={{ color: "#78716c", fontSize: 13 }}>Hämtar…</div>
          )}
          {error && (
            <div
              style={{
                color: "#c0261a",
                fontSize: 13,
                padding: "10px 12px",
                background: "#fdecec",
                borderRadius: 4,
              }}
            >
              {error}
            </div>
          )}

          {!loading && !error && data && (
            <>
              {/* Summary row */}
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  marginBottom: 16,
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    padding: "4px 10px",
                    background: "#f5f5f4",
                    borderRadius: 4,
                    color: "#44403c",
                  }}
                >
                  {data.summary?.n_fields || 0} fält registrerade
                </span>
                {(data.summary?.by_source || []).map((s) => (
                  <span
                    key={s.source}
                    style={{
                      fontSize: 12,
                      padding: "4px 10px",
                      background: "#f0f4fa",
                      borderRadius: 4,
                      color: "#315b8b",
                    }}
                  >
                    {s.source_label}: {s.count}
                  </span>
                ))}
              </div>

              {/* Per-field table */}
              {(data.fields || []).length === 0 ? (
                <div style={{ color: "#78716c", fontSize: 13 }}>
                  Inga datakällor registrerade ännu. Godkänn köparen
                  för att fylla profilen via BolagsAPI + TIC.
                </div>
              ) : (
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 13,
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        borderBottom: "1px solid #e7e5e4",
                        textAlign: "left",
                        color: "#78716c",
                        fontSize: 11,
                        textTransform: "uppercase",
                        letterSpacing: 0.4,
                      }}
                    >
                      <th style={{ padding: "8px 8px 8px 0" }}>Fält</th>
                      <th style={{ padding: "8px 8px" }}>Källa</th>
                      <th style={{ padding: "8px 8px" }}>Uppdaterat</th>
                      <th style={{ padding: "8px 0 8px 8px", textAlign: "right" }}>
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.fields.map((f, idx) => (
                      <tr
                        key={`${f.field_name}-${idx}`}
                        style={{
                          borderBottom: "1px solid #f5f5f4",
                        }}
                      >
                        <td
                          style={{
                            padding: "10px 8px 10px 0",
                            fontWeight: 600,
                          }}
                          title={f.field_name}
                        >
                          {_fieldLabel(f.field_name)}
                        </td>
                        <td style={{ padding: "10px 8px" }}>
                          {f.source_label}
                          {f.confidence != null && (
                            <span
                              style={{
                                fontSize: 11,
                                color: "#78716c",
                                marginLeft: 6,
                              }}
                            >
                              ({Math.round(f.confidence * 100)}%)
                            </span>
                          )}
                        </td>
                        <td
                          style={{
                            padding: "10px 8px",
                            color: "#57534e",
                          }}
                          title={f.enriched_at}
                        >
                          {_formatRelative(f.enriched_at)}
                        </td>
                        <td
                          style={{
                            padding: "10px 0 10px 8px",
                            textAlign: "right",
                          }}
                        >
                          {f.fresh ? (
                            <span
                              style={{
                                fontSize: 11,
                                padding: "2px 8px",
                                background: "#eef7ee",
                                color: "#2f6f34",
                                borderRadius: 3,
                                fontWeight: 600,
                              }}
                            >
                              Aktuell
                            </span>
                          ) : (
                            <span
                              style={{
                                fontSize: 11,
                                padding: "2px 8px",
                                background: "#fef3c7",
                                color: "#92400e",
                                borderRadius: 3,
                                fontWeight: 600,
                              }}
                              title={`TTL ${f.ttl_days} dagar`}
                            >
                              Föråldrad
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AcquirerProvenanceModal;
